import React, { useState, useEffect, useRef } from 'react';
import axios from '../utils/axios';
import { useNavigate } from 'react-router-dom';
import { isAuth } from '../utils/auth';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import { attachLandingInteractions } from '../utils/landingInteractions';
import './EditLanding.css';

const EditLanding = () => {
    const [htmlContent, setHtmlContent] = useState('');
    const [originalContent, setOriginalContent] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const previewRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const checkAuth = async () => {
            const hasSession = await isAuth();
            if (!hasSession) {
                navigate('/login');
            }
        };
        checkAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const loadLanding = async () => {
            try {
                setLoading(true);
                const response = await axios.get('/landing');
                if (response.data.hasCustomContent && response.data.htmlContent) {
                    setHtmlContent(response.data.htmlContent);
                    setOriginalContent(response.data.htmlContent);
                } else {
                    const defaultHtml = getDefaultLandingHTML();
                    setHtmlContent(defaultHtml);
                    setOriginalContent(defaultHtml);
                }
            } catch (error) {
                console.error('Error al cargar landing:', error);
                toast.error('Error al cargar la landing', {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
                const defaultHtml = getDefaultLandingHTML();
                setHtmlContent(defaultHtml);
                setOriginalContent(defaultHtml);
            } finally {
                setLoading(false);
            }
        };
        loadLanding();
    }, []);

    useEffect(() => {
        setHasChanges(htmlContent !== originalContent);
    }, [htmlContent, originalContent]);

    useEffect(() => {
        const preview = previewRef.current;
        if (!preview || !htmlContent) return undefined;

        const attachPreviewInteractions = () => {
            const previewDocument = preview.contentDocument;
            if (!previewDocument?.body) return;
            attachLandingInteractions(previewDocument.body);
        };

        preview.onload = attachPreviewInteractions;
        preview.srcdoc = htmlContent;

        return () => {
            preview.onload = null;
        };
    }, [htmlContent]);

    const getDefaultLandingHTML = () => {
        return `<!DOCTYPE html>
<html lang="es">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Tramites Pronto - Soluciones Integrales en Gestión Administrativa</title>
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet">
    <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css">
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
        .landing-container { min-height: 100vh; background: linear-gradient(135deg, #210B65 0%, #3d2a8a 100%); color: #fff; }
        .container { max-width: 1280px; margin: 0 auto; padding: 0 24px; }
        .features-section-first { padding-top: 48px; }
        .about-section { padding: 80px 24px; position: relative; overflow: hidden; }
        .about-background-pattern { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: radial-gradient(circle at 20% 50%, rgba(255,255,255,0.05) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.05) 0%, transparent 50%), linear-gradient(135deg, #210B65 0%, #3d2a8a 100%); }
        .about-header { text-align: center; max-width: 720px; margin: 0 auto 40px; position: relative; z-index: 1; }
        .about-badge { display: inline-flex; align-items: center; gap: 8px; padding: 8px 18px; background: rgba(255,255,255,0.12); border-radius: 50px; border: 1px solid rgba(255,255,255,0.2); font-size: 0.8125rem; font-weight: 600; margin-bottom: 24px; }
        .about-badge i { color: #ffd700; }
        .about-title { font-size: 2.75rem; font-weight: 800; line-height: 1.15; margin-bottom: 20px; letter-spacing: -0.03em; }
        .about-title-highlight { display: block; margin-top: 6px; }
        .about-description { font-size: 1.0625rem; line-height: 1.7; color: rgba(255,255,255,0.85); }
        .about-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; max-width: 720px; margin: 0 auto 40px; position: relative; z-index: 1; }
        .about-stat { text-align: center; padding: 20px 16px; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15); border-radius: 14px; }
        .about-stat-number { font-size: 1.75rem; font-weight: 800; margin-bottom: 4px; }
        .about-stat-label { font-size: 0.8125rem; color: rgba(255,255,255,0.8); }
        .about-body { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; max-width: 960px; margin: 0 auto; position: relative; z-index: 1; }
        .about-highlights { background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15); border-radius: 16px; padding: 28px; }
        .about-highlights-title { font-size: 1.125rem; font-weight: 700; margin-bottom: 20px; }
        .about-highlights-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 12px; }
        .about-highlights-list li { display: flex; align-items: center; gap: 12px; font-size: 0.9375rem; color: rgba(255,255,255,0.95); }
        .about-highlights-list li i { width: 20px; color: #ffd700; font-size: 0.875rem; }
        .inquiry-box { background: #fff; border-radius: 16px; padding: 32px 28px; color: #1a1a1a; box-shadow: 0 8px 32px rgba(0,0,0,0.16); }
        .inquiry-icon { width: 44px; height: 44px; background: rgba(33,11,101,0.08); border-radius: 12px; display: flex; align-items: center; justify-content: center; margin-bottom: 16px; color: #210B65; font-size: 1.125rem; }
        .inquiry-box h3 { font-size: 1.25rem; font-weight: 700; color: #210B65; margin-bottom: 10px; }
        .inquiry-box p { font-size: 0.9375rem; color: #555; line-height: 1.6; margin-bottom: 20px; }
        .btn-inquiry { background: #25D366; color: #fff; padding: 14px 24px; border-radius: 10px; font-size: 0.9375rem; font-weight: 600; text-decoration: none; display: inline-flex; align-items: center; justify-content: center; border: 2px solid #25D366; transition: all 0.3s ease; }
        .btn-inquiry:hover { background: #fff; color: #25D366; border-color: #25D366; }
        .section-header { text-align: center; margin-bottom: 64px; }
        .section-badge { display: inline-block; padding: 8px 20px; background: rgba(33,11,101,0.1); border-radius: 50px; font-size: 0.875rem; font-weight: 600; color: #210B65; margin-bottom: 16px; text-transform: uppercase; }
        .section-title { font-size: 3rem; font-weight: 800; line-height: 1.2; margin-bottom: 20px; color: #1a1a1a; letter-spacing: -0.03em; }
        .section-subtitle { font-size: 1.125rem; color: #555; line-height: 1.7; max-width: 700px; margin: 0 auto; }
        .features-section { padding: 80px 24px; background: #fff; color: #1a1a1a; }
        .features-section .section-header { margin-bottom: 48px; }
        .features-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; max-width: 1280px; margin: 0 auto; }
        .feature-card { padding: 24px 20px; background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%); border-radius: 16px; text-align: left; transition: all 0.4s ease; border: 2px solid rgba(33,11,101,0.08); height: 100%; display: flex; flex-direction: column; cursor: pointer; position: relative; overflow: hidden; }
        .feature-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px; background: linear-gradient(90deg, #210B65 0%, #3d2a8a 100%); transform: scaleX(0); transition: transform 0.4s ease; }
        .feature-card:hover::before { transform: scaleX(1); }
        .feature-card:hover { transform: translateY(-6px); box-shadow: 0 8px 32px rgba(0,0,0,0.16); border-color: #210B65; }
        .feature-icon { width: 60px; height: 60px; margin-bottom: 16px; background: linear-gradient(135deg, #210B65 0%, #3d2a8a 100%); border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 1.5rem; color: #fff; transition: all 0.3s ease; }
        .feature-card:hover .feature-icon { transform: scale(1.05); }
        .feature-card h3 { font-size: 1.125rem; font-weight: 700; margin-bottom: 8px; color: #210B65; }
        .feature-card p { color: #555; line-height: 1.6; font-size: 0.875rem; margin-bottom: 16px; flex-grow: 1; }
        .feature-link { display: inline-flex; align-items: center; gap: 6px; color: #210B65; font-weight: 600; font-size: 0.8125rem; }
        .feature-link i { font-size: 0.625rem; transition: transform 0.3s ease; }
        .feature-card:hover .feature-link i { transform: translateX(4px); }
        .process-section { padding: 120px 24px; background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%); }
        .process-steps { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; max-width: 1280px; margin: 0 auto; }
        .process-step { text-align: center; padding: 24px 20px; background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%); border-radius: 16px; border: 2px solid rgba(33,11,101,0.08); box-shadow: 0 2px 8px rgba(0,0,0,0.08); transition: all 0.4s ease; height: 100%; display: flex; flex-direction: column; align-items: center; }
        .process-step:hover { transform: translateY(-6px); box-shadow: 0 8px 32px rgba(0,0,0,0.16); border-color: #210B65; }
        .step-number { font-size: 0.8125rem; font-weight: 700; color: #210B65; margin-bottom: 8px; }
        .step-icon { width: 60px; height: 60px; margin: 0 auto 16px; background: linear-gradient(135deg, #210B65 0%, #3d2a8a 100%); border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 1.5rem; color: #fff; }
        .process-step h3 { font-size: 1.125rem; font-weight: 700; margin-bottom: 8px; color: #210B65; }
        .process-step p { font-size: 0.875rem; color: #555; line-height: 1.6; }
        .cta-section { padding: 120px 24px; background: linear-gradient(135deg, #210B65 0%, #3d2a8a 100%); text-align: center; position: relative; }
        .cta-content { max-width: 800px; margin: 0 auto; }
        .cta-content h2 { font-size: 3rem; font-weight: 800; margin-bottom: 24px; line-height: 1.2; }
        .cta-content p { font-size: 1.25rem; margin-bottom: 40px; opacity: 0.95; line-height: 1.7; }
        .cta-buttons { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; }
        .btn-cta-primary { background: #fff; color: #210B65; padding: 18px 40px; font-size: 1.125rem; }
        .btn-cta-secondary { background: rgba(255,255,255,0.1); color: #fff; border: 2px solid rgba(255,255,255,0.3); padding: 18px 40px; font-size: 1.125rem; }
        .landing-footer { padding: 64px 24px 32px; background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%); }
        .footer-content { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; max-width: 1280px; margin: 0 auto 48px; }
        .footer-brand { max-width: 400px; }
        .footer-logo img { max-width: 280px; margin-bottom: 20px; filter: brightness(0) invert(1); }
        .footer-tagline { font-size: 1rem; color: rgba(255,255,255,0.8); }
        .footer-info h4 { font-size: 1.25rem; font-weight: 700; margin-bottom: 24px; }
        .footer-contact-item { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; color: rgba(255,255,255,0.85); font-size: 0.9375rem; }
        .footer-contact-item i { width: 20px; color: #ffd700; }
        .footer-bottom { max-width: 1280px; margin: 0 auto; padding-top: 32px; border-top: 1px solid rgba(255,255,255,0.15); text-align: center; }
        .footer-copyright { font-size: 0.875rem; color: rgba(255,255,255,0.7); }
        .whatsapp-float { position: fixed; bottom: 30px; right: 30px; width: 60px; height: 60px; background: #25D366; border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 15px rgba(37,211,102,0.4); z-index: 1000; transition: transform 0.3s ease; }
        .whatsapp-float:hover { transform: scale(1.2); }
        .whatsapp-float i { color: #fff; font-size: 30px; }
        .btn { display: inline-flex; align-items: center; gap: 10px; padding: 16px 32px; border-radius: 12px; font-size: 1rem; font-weight: 600; text-decoration: none; transition: all 0.3s ease; border: none; cursor: pointer; }
        @media (max-width: 968px) { .about-body { grid-template-columns: 1fr; max-width: 560px; } .features-grid { grid-template-columns: repeat(2, 1fr); } .process-steps { grid-template-columns: repeat(2, 1fr); } .footer-content { grid-template-columns: 1fr; } }
        @media (max-width: 768px) { .about-title { font-size: 2rem; } .about-stats { grid-template-columns: 1fr; max-width: 320px; } .features-grid { grid-template-columns: 1fr; } .process-steps { grid-template-columns: 1fr; } .section-title { font-size: 2rem; } .cta-content h2 { font-size: 2rem; } .btn-inquiry { width: 100%; justify-content: center; } }
    </style>
</head>
<body>
    <div class="landing-container">
        <section id="features" class="features-section features-section-first">
            <div class="container">
                <div class="section-header">
                    <span class="section-badge">Nuestros Servicios</span>
                    <h1 class="section-title">Soluciones Especializadas para tu Negocio</h1>
                    <p class="section-subtitle">Ofrecemos un portafolio completo de servicios diseñados para optimizar tus procesos administrativos y financieros.</p>
                </div>
                <div class="features-grid">
                    <div class="feature-card" data-service="Administración de Pagos">
                        <div class="feature-icon"><i class="fa-solid fa-money-bill-transfer"></i></div>
                        <h3>Administración de Pagos</h3>
                        <p>Gestión profesional integral de pagos, depósitos bancarios y rendiciones. Control total y transparencia en cada operación financiera.</p>
                        <div class="feature-link"><span>Saber más</span><i class="fa-solid fa-arrow-right"></i></div>
                    </div>
                    <div class="feature-card" data-service="Reimpresión de Facturas">
                        <div class="feature-icon"><i class="fa-solid fa-file-invoice"></i></div>
                        <h3>Reimpresión de Facturas</h3>
                        <p>Obtención inmediata de comprobantes fiscales. Proceso ágil, seguro y certificado para todas tus necesidades documentales.</p>
                        <div class="feature-link"><span>Saber más</span><i class="fa-solid fa-arrow-right"></i></div>
                    </div>
                    <div class="feature-card" data-service="Pago de Expensas">
                        <div class="feature-icon"><i class="fa-solid fa-building"></i></div>
                        <h3>Pago de Expensas</h3>
                        <p>Gestión completa y automatizada de expensas y rendiciones. Mantené tus pagos al día sin complicaciones ni demoras.</p>
                        <div class="feature-link"><span>Saber más</span><i class="fa-solid fa-arrow-right"></i></div>
                    </div>
                    <div class="feature-card" data-service="Planes Municipales">
                        <div class="feature-icon"><i class="fa-solid fa-city"></i></div>
                        <h3>Planes Municipales</h3>
                        <p>Asesoramiento especializado y gestión eficiente de planes municipales. Facilitamos tus trámites con entidades gubernamentales locales.</p>
                        <div class="feature-link"><span>Saber más</span><i class="fa-solid fa-arrow-right"></i></div>
                    </div>
                    <div class="feature-card" data-service="Planes ARBA">
                        <div class="feature-icon"><i class="fa-solid fa-file-contract"></i></div>
                        <h3>Planes ARBA</h3>
                        <p>Administración eficiente y cumplimiento tributario de planes ARBA. Soluciones adaptadas para evitar complicaciones y demoras.</p>
                        <div class="feature-link"><span>Saber más</span><i class="fa-solid fa-arrow-right"></i></div>
                    </div>
                    <div class="feature-card" data-service="Pagos AGIP">
                        <div class="feature-icon"><i class="fa-solid fa-receipt"></i></div>
                        <h3>Pagos AGIP</h3>
                        <p>Gestión integral de pagos AGIP y servicios tributarios. Soluciones personalizadas adaptadas a las necesidades de tu organización.</p>
                        <div class="feature-link"><span>Saber más</span><i class="fa-solid fa-arrow-right"></i></div>
                    </div>
                    <div class="feature-card" data-service="Servicios Públicos">
                        <div class="feature-icon"><i class="fa-solid fa-droplet"></i></div>
                        <h3>Servicios Públicos</h3>
                        <p>Pagos centralizados de EDEA, OSSE, AySA y más. Una única gestión para todos tus servicios públicos, simplificando tu administración.</p>
                        <div class="feature-link"><span>Saber más</span><i class="fa-solid fa-arrow-right"></i></div>
                    </div>
                    <div class="feature-card" data-service="Gestoría del Automotor">
                        <div class="feature-icon"><i class="fa-solid fa-car"></i></div>
                        <h3>Gestoría del Automotor</h3>
                        <p>Transferencias, prendas, inscripciones 0 km y más. Expertos certificados en trámites vehiculares con resultados garantizados y tiempos optimizados.</p>
                        <div class="feature-link"><span>Saber más</span><i class="fa-solid fa-arrow-right"></i></div>
                    </div>
                    <div class="feature-card" data-service="Trámites en Registro de la Propiedad Inmueble">
                        <div class="feature-icon"><i class="fa-solid fa-landmark"></i></div>
                        <h3>Trámites en Registro de la Propiedad Inmueble</h3>
                        <p>Gestión de trámites ante el Registro de la Propiedad Inmueble. Informe de dominio y demás gestiones registrales con agilidad y seguridad.</p>
                        <div class="feature-link"><span>Saber más</span><i class="fa-solid fa-arrow-right"></i></div>
                    </div>
                    <div class="feature-card" data-service="Solicitud de Partidas">
                        <div class="feature-icon"><i class="fa-solid fa-scroll"></i></div>
                        <h3>Solicitud de Partidas</h3>
                        <p>Obtención de partidas de nacimiento, matrimonio y defunción. Tramitamos tus certificados vitales de forma rápida y sin complicaciones.</p>
                        <div class="feature-link"><span>Saber más</span><i class="fa-solid fa-arrow-right"></i></div>
                    </div>
                </div>
            </div>
        </section>

        <section id="about" class="about-section">
            <div class="about-background-pattern"></div>
            <div class="container">
                <div class="about-header">
                    <div class="about-badge"><i class="fa-solid fa-award"></i><span>Más de 30 años de experiencia</span></div>
                    <h2 class="about-title">Soluciones Integrales en<span class="about-title-highlight"> Gestión Administrativa</span></h2>
                    <p class="about-description">Optimizamos tus procesos administrativos con servicios especializados que te permiten enfocarte en lo que realmente importa. Experiencia, eficiencia y compromiso en cada gestión.</p>
                </div>
                <div class="about-stats">
                    <div class="about-stat"><div class="about-stat-number">+500</div><div class="about-stat-label">Clientes satisfechos</div></div>
                    <div class="about-stat"><div class="about-stat-number">+10K</div><div class="about-stat-label">Trámites gestionados</div></div>
                    <div class="about-stat"><div class="about-stat-number">24/7</div><div class="about-stat-label">Atención disponible</div></div>
                </div>
                <div class="about-body">
                    <div class="about-highlights">
                        <h3 class="about-highlights-title">Servicio Integral</h3>
                        <ul class="about-highlights-list">
                            <li><i class="fa-solid fa-check"></i>Gestión completa y profesional</li>
                            <li><i class="fa-solid fa-check"></i>Trámites automotores especializados</li>
                            <li><i class="fa-solid fa-check"></i>Servicios municipales y tributarios</li>
                            <li><i class="fa-solid fa-check"></i>Atención personalizada y dedicada</li>
                            <li><i class="fa-solid fa-star"></i>Certificados y garantizados</li>
                        </ul>
                    </div>
                    <div class="inquiry-box">
                        <div class="inquiry-icon"><i class="fa-solid fa-message"></i></div>
                        <h3>¿No encontraste lo que buscabas?</h3>
                        <p>Enviános un mensaje con tu consulta y nuestro equipo te responderá a la brevedad.</p>
                        <a href="https://wa.me/541166371898?text=Hola%2C%20no%20encontr%C3%A9%20lo%20que%20buscaba%20en%20la%20web.%20Quisiera%20hacer%20una%20consulta." target="_blank" rel="noopener noreferrer" class="btn-inquiry">Consultar</a>
                    </div>
                </div>
            </div>
        </section>

        <section class="process-section">
            <div class="container">
                <div class="section-header">
                    <span class="section-badge">Cómo Trabajamos</span>
                    <h2 class="section-title">Proceso Simple y Eficiente</h2>
                    <p class="section-subtitle">Nuestro método probado garantiza resultados rápidos y confiables.</p>
                </div>
                <div class="process-steps">
                    <div class="process-step">
                        <div class="step-number">1</div>
                        <div class="step-icon"><i class="fa-solid fa-comments"></i></div>
                        <h3>Consulta Inicial</h3>
                        <p>Contactanos y describinos tu necesidad. Nuestro equipo analizará tu caso y te propondrá la mejor solución.</p>
                    </div>
                    <div class="process-step">
                        <div class="step-number">2</div>
                        <div class="step-icon"><i class="fa-solid fa-file-signature"></i></div>
                        <h3>Documentación</h3>
                        <p>Te guiamos en la recopilación de documentos necesarios. Proceso simple y transparente.</p>
                    </div>
                    <div class="process-step">
                        <div class="step-number">3</div>
                        <div class="step-icon"><i class="fa-solid fa-gears"></i></div>
                        <h3>Gestión</h3>
                        <p>Nuestros expertos procesan tu trámite con eficiencia y seguimiento constante.</p>
                    </div>
                    <div class="process-step">
                        <div class="step-number">4</div>
                        <div class="step-icon"><i class="fa-solid fa-check-double"></i></div>
                        <h3>Entrega</h3>
                        <p>Recibís tu trámite completado con documentación y seguimiento post-entrega.</p>
                    </div>
                </div>
            </div>
        </section>

        <footer class="landing-footer">
            <div class="container">
                <div class="footer-content">
                    <div class="footer-brand">
                        <div class="footer-logo"><img src="/static/media/tramites.ba023e4b9755f914fbd1.png" alt="Tramites Pronto" /></div>
                        <p class="footer-tagline">Tu aliado confiable en gestión administrativa</p>
                    </div>
                    <div class="footer-info">
                        <h4>Información de Contacto</h4>
                        <div class="footer-contact-item"><i class="fa-solid fa-envelope"></i><span>Tramitespronto02@gmail.com</span></div>
                        <div class="footer-contact-item"><i class="fa-solid fa-location-dot"></i><span>Moine 1499 (1661) - Bella vista, Pcia. de Bs. As</span></div>
                        <div class="footer-contact-item"><i class="fa-brands fa-whatsapp"></i><span>+54 11 6637-1898</span></div>
                    </div>
                </div>
                <div class="footer-bottom">
                    <p class="footer-copyright">© ${new Date().getFullYear()} Tramites Pronto. Todos los derechos reservados.</p>
                </div>
            </div>
        </footer>
    </div>

    <a href="https://wa.me/541166371898" target="_blank" rel="noopener noreferrer" class="whatsapp-float">
        <i class="fa-brands fa-whatsapp"></i>
    </a>
</body>
</html>`;
    };

    const handleSave = async () => {
        if (!hasChanges) {
            toast.info('No hay cambios para guardar', {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
            return;
        }

        const result = await Swal.fire({
            title: '¿Guardar cambios?',
            text: 'Esto actualizará la landing page visible para todos los usuarios',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, guardar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#210B65'
        });

        if (!result.isConfirmed) return;

        try {
            setSaving(true);
            await axios.post('/landing', { htmlContent });
            setOriginalContent(htmlContent);
            setHasChanges(false);
            toast.success('Landing guardada correctamente', {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
        } catch (error) {
            console.error('Error al guardar:', error);
            toast.error('Error al guardar la landing', {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        const result = await Swal.fire({
            title: '¿Restablecer a la versión por defecto?',
            text: 'Esto eliminará todos los cambios personalizados',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, restablecer',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#dc3545'
        });

        if (!result.isConfirmed) return;

        try {
            setSaving(true);
            await axios.post('/landing/reset');
            const defaultHtml = getDefaultLandingHTML();
            setHtmlContent(defaultHtml);
            setOriginalContent(defaultHtml);
            setHasChanges(false);
            toast.success('Landing restablecida correctamente', {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
        } catch (error) {
            console.error('Error al restablecer:', error);
            toast.error('Error al restablecer la landing', {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="edit-landing-loading">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="edit-landing-container">
            <ToastContainer />
            <div className="edit-landing-header">
                <h2>Editor de Landing Page</h2>
                <div className="edit-landing-actions">
                    <button
                        onClick={handleReset}
                        className="btn btn-outline-danger btn-sm"
                        disabled={saving}
                    >
                        <i className="fa-solid fa-rotate-left me-2"></i>
                        Restablecer
                    </button>
                    <button
                        onClick={handleSave}
                        className="btn btn-primary btn-sm"
                        disabled={saving || !hasChanges}
                    >
                        {saving ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                Guardando...
                            </>
                        ) : (
                            <>
                                <i className="fa-solid fa-save me-2"></i>
                                Guardar Cambios
                            </>
                        )}
                    </button>
                </div>
            </div>

            <div className="edit-landing-content">
                <div className="edit-landing-editor">
                    <div className="editor-header">
                        <h3>Editor HTML</h3>
                        {hasChanges && (
                            <span className="badge bg-warning text-dark">Cambios sin guardar</span>
                        )}
                    </div>
                    <textarea
                        className="form-control editor-textarea"
                        value={htmlContent}
                        onChange={(e) => setHtmlContent(e.target.value)}
                        spellCheck={false}
                    />
                </div>

                <div className="edit-landing-preview">
                    <div className="preview-header">
                        <h3>Vista Previa</h3>
                        <button
                            onClick={() => {
                                if (previewRef.current) {
                                    previewRef.current.srcdoc = htmlContent;
                                }
                            }}
                            className="btn btn-sm btn-outline-secondary"
                        >
                            <i className="fa-solid fa-refresh me-2"></i>
                            Actualizar
                        </button>
                    </div>
                    <iframe
                        ref={previewRef}
                        title="Preview"
                        className="preview-iframe"
                        sandbox="allow-same-origin allow-scripts"
                    />
                </div>
            </div>
        </div>
    );
};

export default EditLanding;

